import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle, Ruler } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomOrderPage() {
  const [searchParams] = useSearchParams();

  // Initialise form state from URL params on first render (lazy init avoids
  // the set-state-in-effect anti-pattern entirely).
  const initialText = searchParams.get('text') || '';
  const initialStyleRaw = searchParams.get('style');
  const initialStyle = (() => {
    if (!initialStyleRaw || !['gold', 'silver', 'roseGold'].includes(initialStyleRaw)) return 'gold';
    return initialStyleRaw === 'roseGold' ? 'rose-gold' : initialStyleRaw;
  })();
  const initialBuilderStats = (() => {
    const total = searchParams.get('totalBeads');
    const filler = searchParams.get('fillerBeads');
    const size = searchParams.get('size');
    if (!total && !filler && !size) return null;
    return {
      totalBeads: parseInt(total, 10) || 0,
      fillerBeads: parseInt(filler, 10) || 0,
      dots: parseInt(searchParams.get('dots'), 10) || 0,
      dashes: parseInt(searchParams.get('dashes'), 10) || 0,
      sizeCm: size ? parseFloat(size) : null,
    };
  })();

  const [customText, setCustomText] = useState(initialText);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bracelet_style: initialStyle,
    clasp: 'lobster',
    quantity: 1,
    special_instructions: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const prePopulated = Boolean(initialText);
  const [builderStats] = useState(initialBuilderStats);

  // Calculate price
  const basePrice = 55.00;
  const extraPerChar = 2.00;
  const textLength = customText.replace(/\s/g, '').length;
  const calculatedPrice = basePrice + Math.max(0, (textLength - 8)) * extraPerChar;
  const totalPrice = calculatedPrice * formData.quantity;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customText.trim()) {
      toast.error('Please enter your custom text');
      return;
    }

    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const sizeLine = builderStats && builderStats.sizeCm
        ? `Bracelet size: ${builderStats.sizeCm.toFixed(1)} cm · `
        : '';
      const instructionsForMaker = builderStats
        ? `[From Bracelet Builder] ${sizeLine}Total beads: ${builderStats.totalBeads} · Dots: ${builderStats.dots} · Dashes: ${builderStats.dashes} · Cream filler beads (to pad to length): ${builderStats.fillerBeads}\n\n${formData.special_instructions || ''}`.trim()
        : formData.special_instructions;

      const response = await axios.post(`${API}/orders/custom`, {
        ...formData,
        special_instructions: instructionsForMaker,
        custom_text: customText
      });
      
      setOrderDetails(response.data);
      setSubmitted(true);
      toast.success('Custom order submitted successfully!');
    } catch (error) {
      logger.error('Error submitting order:', error);
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted && orderDetails) {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="custom-order-success">
      <Seo title="Order your custom bracelet" description={"Tell Lindy what you'd like written in Morse code, your wrist size, clasp choice, and bead colour. Hand-strung from her Paphos studio."} path="/custom" />
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#354F52] rounded-full flex items-center justify-center mx-auto mb-8">
              <Sparkles className="w-10 h-10 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4">
              Order Submitted!
            </h1>
            
            <p className="text-[#52796F] mb-8">
              Thank you for your custom order. We&apos;ll start crafting your bracelet soon.
            </p>

            <div className="bg-[#F5F5F0] p-8 rounded-sm text-left mb-8">
              <h2 className="font-serif text-xl text-[#354F52] mb-4">Order Details</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-caption text-[#84A98C] mb-1">Your Message</p>
                  <p className="text-[#354F52] font-medium text-lg">{orderDetails.custom_text}</p>
                </div>
                
                <div>
                  <p className="text-caption text-[#84A98C] mb-2">Morse Code</p>
                  <MorseCodeVisualizer text={orderDetails.custom_text} size="md" />
                </div>
                
                <div className="pt-4 border-t border-[#CAD2C5]/50">
                  <div className="flex justify-between">
                    <span className="text-[#52796F]">Total Price</span>
                    <span className="text-[#D4AF37] font-medium text-xl">
                      €{orderDetails.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-[#84A98C]">
              We&apos;ll contact you at your email with payment and shipping details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16" data-testid="custom-order-page">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Create Your Own</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Custom Order
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Type your word or phrase below and watch it transform into 
            a beautiful Morse code bracelet.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Pre-populated notice */}
          {prePopulated && customText && (
            <div className="bg-[#52796F]/10 border border-[#52796F]/30 p-4 rounded-sm mb-8 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#52796F] flex-shrink-0" strokeWidth={1.5} />
              <p className="text-[#354F52]">
                <span className="font-medium">Your design from the Bracelet Builder has been loaded!</span>
                <span className="text-[#52796F] ml-1">Just fill in your details below to complete your order.</span>
              </p>
            </div>
          )}

          {/* Bead counts from the Builder (read-only) */}
          {builderStats && (
            <div
              className="bg-[#354F52] text-[#FAF9F6] p-6 rounded-sm mb-8"
              data-testid="builder-stats-card"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Ruler className="w-5 h-5 text-[#354F52]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-caption text-[#D4AF37] mb-1">From the Bracelet Builder</p>
                  <p className="font-serif text-lg leading-tight">
                    {builderStats.sizeCm
                      ? `Your design fits a ${builderStats.sizeCm.toFixed(1)} cm bracelet — exact bead counts saved for the maker.`
                      : 'Your design fits — exact bead counts saved for the maker.'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-xs text-[#CAD2C5]">
                <div data-testid="stats-dots">
                  <p className="font-serif text-2xl text-[#FAF9F6]">{builderStats.dots}</p>
                  <p>Dots</p>
                </div>
                <div data-testid="stats-dashes">
                  <p className="font-serif text-2xl text-[#FAF9F6]">{builderStats.dashes}</p>
                  <p>Dashes</p>
                </div>
                <div data-testid="stats-filler">
                  <p className="font-serif text-2xl text-[#E5DCC5]">{builderStats.fillerBeads}</p>
                  <p>Cream filler</p>
                </div>
                <div data-testid="stats-total">
                  <p className="font-serif text-2xl text-[#D4AF37]">{builderStats.totalBeads}</p>
                  <p>Total beads</p>
                </div>
              </div>
              <p className="text-xs text-[#84A98C] mt-4">
                {builderStats.sizeCm
                  ? `These counts (and your ${builderStats.sizeCm.toFixed(1)} cm size) will be sent to Lindy so she can prep the exact beads.`
                  : 'These counts will be sent to Lindy with your order so she can prep the exact beads.'}
              </p>
            </div>
          )}

          {/* Interactive Builder */}
          <div className="bg-[#F5F5F0] p-8 md:p-12 rounded-sm mb-12">
            <div className="text-center mb-8">
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value.slice(0, 20))}
                placeholder="Type your word..."
                className="input-underline max-w-md"
                maxLength={20}
                data-testid="custom-text-input"
              />
              <p className="text-sm text-[#84A98C] mt-2">
                {customText.length}/20 characters
              </p>
            </div>

            {/* Live Preview */}
            <div className="bg-[#FAF9F6] p-8 rounded-sm">
              <p className="text-caption text-[#84A98C] text-center mb-4">
                Your Bracelet Preview
              </p>
              <MorseCodeVisualizer text={customText} size="lg" />
            </div>

            {/* Price Preview */}
            {customText && (
              <div className="mt-8 text-center">
                <p className="text-sm text-[#84A98C] mb-2">Estimated Price</p>
                <p className="text-3xl font-serif text-[#D4AF37]">
                  €{calculatedPrice.toFixed(2)}
                </p>
                {textLength > 8 && (
                  <p className="text-xs text-[#84A98C] mt-1">
                    Base: €55.00 + €{((textLength - 8) * extraPerChar).toFixed(2)} for extra characters
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Order Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#354F52]">
                  Your Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52]"
                  required
                  data-testid="order-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#354F52]">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52]"
                  required
                  data-testid="order-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="style" className="text-[#354F52]">
                  Bracelet Style
                </Label>
                <Select
                  value={formData.bracelet_style}
                  onValueChange={(value) => setFormData({ ...formData, bracelet_style: value })}
                >
                  <SelectTrigger 
                    className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52]"
                    data-testid="order-style"
                  >
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gold">Gold Beads</SelectItem>
                    <SelectItem value="silver">Silver Beads</SelectItem>
                    <SelectItem value="rose-gold">Rose Gold Beads</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clasp" className="text-[#354F52]">
                  Clasp
                </Label>
                <Select
                  value={formData.clasp}
                  onValueChange={(value) => setFormData({ ...formData, clasp: value })}
                >
                  <SelectTrigger
                    className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52]"
                    data-testid="order-clasp"
                  >
                    <SelectValue placeholder="Choose a clasp" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lobster">Lobster Claw — classic &amp; secure</SelectItem>
                    <SelectItem value="drawstring">Drawstring — boho, infinitely sizeable</SelectItem>
                    <SelectItem value="magnetic-heart">Magnetic Heart — snap-shut</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-[#354F52]">
                  Quantity
                </Label>
                <Select
                  value={formData.quantity.toString()}
                  onValueChange={(value) => setFormData({ ...formData, quantity: parseInt(value) })}
                >
                  <SelectTrigger 
                    className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52]"
                    data-testid="order-quantity"
                  >
                    <SelectValue placeholder="Select quantity" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions" className="text-[#354F52]">
                Special Instructions (Optional)
              </Label>
              <Textarea
                id="instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                className="border-[#CAD2C5] focus:border-[#354F52] focus:ring-[#354F52] min-h-[100px]"
                placeholder="Any special requests or notes... For example: 'My wrist measures 16 cm — please size for a snug fit.'"
                data-testid="order-instructions"
              />
              <p className="text-xs text-[#84A98C]">
                Need help with sizing? Read the{' '}
                <Link
                  to="/sizing-guide"
                  className="underline decoration-[#D4AF37]/40 underline-offset-2 hover:text-[#D4AF37] transition-colors"
                  data-testid="custom-order-sizing-guide-link"
                >
                  60-second wrist measuring guide
                </Link>{' '}
                — Lindy will use the size you mention here.
              </p>
            </div>

            {/* Order Summary */}
            {customText && (
              <div className="bg-[#F5F5F0] p-6 rounded-sm">
                <h3 className="font-serif text-lg text-[#354F52] mb-4">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#52796F]">Custom Text</span>
                    <span className="text-[#354F52] font-medium">{customText}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#52796F]">Style</span>
                    <span className="text-[#354F52] capitalize">{formData.bracelet_style}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#52796F]">Quantity</span>
                    <span className="text-[#354F52]">{formData.quantity}</span>
                  </div>
                  <div className="pt-4 mt-4 border-t border-[#CAD2C5]/50 flex justify-between">
                    <span className="text-[#354F52] font-medium">Total</span>
                    <span className="text-[#D4AF37] font-medium text-xl">
                      €{totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !customText}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="submit-custom-order"
            >
              {loading ? 'Submitting...' : 'Submit Custom Order'}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
